import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import SelfMonitorApi from '../../../api/SelfMonitorApi';
import { Dropdown, DropdownOption } from '../../../components/forms';

const POLL_INTERVAL_MS = 3000;
const MAX_LINES = 5000;
// The console stretches to the bottom of the viewport; this leaves room for
// the in-flow #footer (~60px) plus a little breathing space below the card.
const FOOTER_GAP_PX = 76;
const MIN_CONSOLE_HEIGHT_PX = 240;

type LogLevel = 'ALL' | 'SEVERE' | 'WARNING' | 'INFO' | 'CONFIG' | 'FINE';

// Tomcat/JUL log lines put the level as a bare word after the timestamp
// (e.g. "11-Sep-2026 20:10:12.068 INFO [main] ...") - the brackets that
// follow wrap the thread name, not the level, so patterns must anchor on
// position rather than looking for "[LEVEL]". Levels follow JUL's order
// (SEVERE > WARNING > INFO > CONFIG > FINE > FINER > FINEST); FINER/FINEST
// are folded into the FINE bucket rather than given their own option.
//
// SEVERE additionally covers the other log sources the file dropdown can
// select (see groupFiles) since ERROR/FATAL are how they report the same
// severity, each in its own format:
//  - PostgreSQL ("... [12345] ERROR:  ..."): word+colon right after [pid].
//  - Nginx ("... [error] 1234#0: ..."): bracketed, lowercase.
const LEVEL_PATTERNS: Record<Exclude<LogLevel, 'ALL'>, RegExp> = {
    SEVERE: /^\S+\s+\S+\s+(SEVERE|ERROR|FATAL)\b|\[\d+\]\s+(ERROR|FATAL):|\[(ERROR|FATAL)\]/i,
    WARNING: /^\S+\s+\S+\s+WARN(ING)?\b/i,
    INFO: /^\S+\s+\S+\s+INFO\b/i,
    CONFIG: /^\S+\s+\S+\s+CONFIG\b/i,
    FINE: /^\S+\s+\S+\s+(FINE|FINER|FINEST|DEBUG)\b/i,
};

const LEVEL_OPTIONS: DropdownOption[] = [
    { value: 'ALL', label: 'All Levels' },
    { value: 'SEVERE', label: 'Severe' },
    { value: 'WARNING', label: 'Warning' },
    { value: 'INFO', label: 'Info' },
    { value: 'CONFIG', label: 'Config' },
    { value: 'FINE', label: 'Fine' },
];

const levelClass =(line: string): string => {
    if (LEVEL_PATTERNS.SEVERE.test(line)) return 'log-line-severe';
    if (LEVEL_PATTERNS.WARNING.test(line)) return 'log-line-warning';
    return '';
};

// The server namespaces non-Tomcat log files as "<source>/<filename>" (see
// SelfMonitorServlet#logSources) so the dropdown can group them; Tomcat's
// own files stay bare for backward compatibility.
const GROUP_LABELS: Record<string, string> = {
    postgresql: 'PostgreSQL',
    nginx: 'Nginx',
};

const groupLabel = (file: string): string => {
    const slash = file.indexOf('/');
    return slash < 0 ? 'Tomcat' : (GROUP_LABELS[file.slice(0, slash)] || 'Tomcat');
};

const displayName = (file: string): string => {
    const slash = file.indexOf('/');
    return slash < 0 ? file : file.slice(slash + 1);
};

const logTimestamp = (file: string): string | undefined => {
    const match = displayName(file).match(/(\d{4}-\d{2}-\d{2})(?:_(\d{6}))?/);
    return match ? `${match[1]}_${match[2] || '235959'}` : undefined;
};

const sortFilesNewestFirst = (files: string[]): string[] => [...files].sort((a, b) => {
    const aTimestamp = logTimestamp(a);
    const bTimestamp = logTimestamp(b);
    // Undated names are the active logs (for example catalina.out), so keep
    // them ahead of rotated, dated files within their source group.
    if (!aTimestamp && bTimestamp) return -1;
    if (aTimestamp && !bTimestamp) return 1;
    if (aTimestamp && bTimestamp && aTimestamp !== bTimestamp) return bTimestamp.localeCompare(aTimestamp);
    return displayName(b).localeCompare(displayName(a));
});

// Groups files by source while preserving the server's ordering (Tomcat,
// then PostgreSQL, then Nginx), and sorts newest files first within each group.
const groupFiles = (files: string[]): Array<[string, string[]]> => {
    const groups = new Map<string, string[]>();

    for (const file of files) {
        const label = groupLabel(file);
        const group = groups.get(label);
        if (group) {
            group.push(file);
        } else {
            groups.set(label, [file]);
        }
    }

    return Array.from(groups.entries()).map(([label, group]): [string, string[]] => [label, sortFilesNewestFirst(group)]);
};

// Tails a whitelisted Tomcat log file by polling for new bytes at an
// offset - there is no SSE/WebSocket anywhere in this app, and nginx
// proxies /archiver/ with buffering and a 60s read timeout, so a held-open
// streaming request is not a good fit here.
const LogsTab: React.FC = () => {
    const [files, setFiles] = useState<string[]>([]);
    const [selectedFile, setSelectedFile] = useState<string>('');
    const [lines, setLines] = useState<string[]>([]);
    const [level, setLevel] = useState<LogLevel>('ALL');
    const [search, setSearch] = useState('');
    const [paused, setPaused] = useState(false);
    const [autoScroll, setAutoScroll] = useState(true);
    const [wrap, setWrap] = useState(true);
    const [rotatedNotice, setRotatedNotice] = useState(false);

    // Byte offset into the file and any trailing partial line carried over
    // to the next poll - refs so polling doesn't need to be re-created on
    // every tick.
    const offsetRef = useRef<number>(0);
    const partialLineRef = useRef<string>('');
    const requestIdRef = useRef(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const [consoleHeight, setConsoleHeight] = useState<number>();

    // Sized from the console's actual position rather than a fixed
    // calc(100vh - N) because what sits above it varies - the toolbar wraps
    // on narrow screens and the rotation notice comes and goes.
    useLayoutEffect(() => {
        const fitConsole = () => {
            if (!containerRef.current) {
                return;
            }
            const top = containerRef.current.getBoundingClientRect().top + window.scrollY;
            setConsoleHeight(Math.max(MIN_CONSOLE_HEIGHT_PX, window.innerHeight - top - FOOTER_GAP_PX));
        };

        fitConsole();
        window.addEventListener('resize', fitConsole);

        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fitConsole) : undefined;
        if (observer && headerRef.current) {
            observer.observe(headerRef.current);
        }

        return () => {
            window.removeEventListener('resize', fitConsole);
            observer?.disconnect();
        };
    }, [rotatedNotice]);

    useEffect(() => {
        (async () => {
            const fileList = await SelfMonitorApi.listLogFiles();
            setFiles(fileList);
            if (fileList.length > 0) {
                // The application's own logging goes to localhost.<date>.log,
                // not catalina.out - default to the newest of those when present,
                // since the server's listing order isn't by date.
                const [newestLocalhost] = sortFilesNewestFirst(fileList.filter(f => f.startsWith('localhost.')));
                const preferred = newestLocalhost || fileList[0];
                setSelectedFile(preferred);
            }
        })();
    }, []);

    useEffect(() => {
        offsetRef.current = 0;
        partialLineRef.current = '';
        setLines([]);
    }, [selectedFile]);

    const poll = useCallback(async () => {
        if (!selectedFile) {
            return;
        }

        const reqId = ++requestIdRef.current;
        const result = await SelfMonitorApi.tailLog(selectedFile, offsetRef.current || undefined);

        if (!result || reqId !== requestIdRef.current || result.error) {
            return;
        }

        if (result.truncated) {
            setRotatedNotice(true);
            setTimeout(() => setRotatedNotice(false), 5000);
            setLines([]);
            partialLineRef.current = '';
        }

        offsetRef.current = result.nextoffset;
        const combined = partialLineRef.current + result.text;
        const parts = combined.split('\n');
        partialLineRef.current = parts.pop() || '';

        if (parts.length > 0) {
            setLines(prev => {
                const next = [...prev, ...parts];
                return next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next;
            });
        }
    }, [selectedFile]);

    useEffect(() => {
        if (paused || !selectedFile) {
            return;
        }

        poll();
        const timer = setInterval(poll, POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [paused, selectedFile, poll]);

    useEffect(() => {
        if (autoScroll && containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    }, [lines, autoScroll]);

    const handleScroll = () => {
        if (!containerRef.current) {
            return;
        }

        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        setAutoScroll (scrollHeight - scrollTop - clientHeight < 20);
    };

    const filteredLines = lines.filter(line => {
        if (level !== 'ALL' && !LEVEL_PATTERNS[level].test(line)) return false;
        if (search && !line.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    return (
        <div className="self-monitor-logs">
            <div className="card">
                <div className="card-header" ref={headerRef}>
                    <div className="self-monitor-logs__toolbar">
                        <Dropdown
                            ariaLabel="Select log file"
                            className="self-monitor-logs__file"
                            value={selectedFile}
                            onChange={value => setSelectedFile(String(value))}
                            placeholder="No log files found"
                            options={groupFiles(files).map(([label, group]) => ({
                                label,
                                items: group.map(file => ({ value: file, label: displayName(file) })),
                            }))}
                        />
                        <Dropdown
                            ariaLabel="Select log level"
                            className="self-monitor-logs__level"
                            value={level}
                            onChange={value => setLevel(value as LogLevel)}
                            options={LEVEL_OPTIONS}
                        />
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search logs..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <span className="self-monitor-logs__count">{filteredLines.length} entries</span>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setPaused(p => !p)}
                        >
                            <i className={`fal ${paused ? 'fa-play' : 'fa-pause'} fa-fw`} aria-hidden="true" />
                            {paused ? 'Resume' : 'Pause'}
                        </button>
                        <button
                            type="button"
                            className={`btn btn-sm btn-outline-secondary ${wrap ? 'active' : ''}`}
                            aria-pressed={wrap}
                            title={wrap ? 'Lines wrap to fit the console' : 'Long lines scroll horizontally'}
                            onClick={() => setWrap(w => !w)}
                        >
                            <i className="fal fa-text-width fa-fw" aria-hidden="true" />
                            Wrap
                        </button>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setLines([])}
                        >
                            Clear
                        </button>
                    </div>
                </div>
                <div className="card-body p-0">
                    {rotatedNotice && (
                        <div className="self-monitor-logs__notice">
                            Log file rotated or truncated - showing new content from the start.
                        </div>
                    )}
                    <div
                        className={`self-monitor-logs__console ${wrap ? '' : 'self-monitor-logs__console--nowrap'}`}
                        ref={containerRef}
                        style={{ height: consoleHeight }}
                        onScroll={handleScroll}
                    >
                        {filteredLines.length === 0 && (
                            <div className="self-monitor-logs__empty text-muted">No log entries to display.</div>
                        )}
                        {filteredLines.map((line, i) => (
                            <div key={i} className={`self-monitor-logs__line ${levelClass(line)}`}>{line}</div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LogsTab;
